import React, {useEffect, useRef, useState} from "react";

import {MediaItem, MediaKind, MediaType} from "../../services/menu/models";
import styles from "./mediaFrame.module.sass";

/**
 * Один элемент медиа позиции.
 *
 * Ключевое различие — `playable`: движение разрешено только в открытой карточке.
 * В списке, корзине и меню категорий гость видит неподвижный первый кадр, и это
 * не то же самое, что `type`: корзина тоже просит `regular`, но проигрывать там нечего.
 */

export type MediaFrameVariant = "fill" | "inline";

interface MediaFrameProps {
    media: MediaItem;
    type: MediaType;
    playable: boolean;
    variant: MediaFrameVariant;
    lazy: boolean;
    onReady?: () => void;
}

const resolveMediaUrl = (media: MediaItem, type: MediaType): string => {
    // Проверяем именно на gif/video, а не на !== image: если бэк старый и kind не пришёл,
    // отдать source вместо нарезки — это оригинал в полном разрешении во всех списках.
    if (media.kind === MediaKind.gif || media.kind === MediaKind.video) return media.source;

    if (type === MediaType.preview && media.previewHighRes) return media.previewHighRes;
    if (type === MediaType.regular && media.regularHighRes) return media.regularHighRes;

    return media.source;
};

/**
 * Гифку в <img> не остановить, поэтому в списке рисуем её первый кадр в канвас.
 * Пиксели обратно не читаем, так что «испорченный» кросс-доменной картинкой канвас не мешает.
 */
const GifFirstFrame = ({src, className, onReady}: {
    src: string,
    className: string,
    onReady?: () => void
}) => {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const onReadyRef = useRef(onReady);
    onReadyRef.current = onReady;

    useEffect(() => {
        const image = new Image();

        image.onload = () => {
            const canvas = canvasRef.current;

            if (canvas) {
                canvas.width = image.naturalWidth;
                canvas.height = image.naturalHeight;
                canvas.getContext("2d")?.drawImage(image, 0, 0);
            }

            onReadyRef.current?.();
        };
        image.onerror = () => onReadyRef.current?.();
        image.src = src;
    }, [src]);

    return <canvas ref={canvasRef} className={className}/>;
};

/**
 * Видео в карточке: до нажатия — тот же неподвижный первый кадр, что и в списке,
 * поверх него кнопка. Старт по тапу снимает все вопросы с автоплеем — звук в этот
 * момент разрешён любым браузером, и `muted` не нужен.
 */
const PlayableVideo = ({src, className, onReady}: {
    src: string,
    className: string,
    onReady?: () => void
}) => {
    const videoRef = useRef<HTMLVideoElement>(null);
    const [playing, setPlaying] = useState(false);

    const toggle = (e: React.MouseEvent) => {
        e.stopPropagation();

        const video = videoRef.current;
        if (!video) return;

        if (video.paused) {
            video.play();
        } else {
            video.pause();
        }
    };

    return (
        <>
            <video
                ref={videoRef}
                className={className}
                src={`${src}#t=0.1`}
                preload="metadata"
                playsInline
                onClick={toggle}
                onPlay={() => setPlaying(true)}
                onPause={() => setPlaying(false)}
                onEnded={() => setPlaying(false)}
                onLoadedMetadata={onReady}
                onError={onReady}
            />
            {!playing && (
                <button type="button" className={styles.playButton} onClick={toggle}
                        aria-label="Проиграть видео">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M8 5v14l11-7z"/>
                    </svg>
                </button>
            )}
        </>
    );
};

const MediaFrame = ({media, type, playable, variant, lazy, onReady}: MediaFrameProps) => {
    const className = variant === "fill" ? styles.fill : styles.inline;

    if (media.kind === MediaKind.video) {
        if (playable) {
            return <PlayableVideo src={media.source} className={className} onReady={onReady}/>;
        }

        // Якорь #t=0.1 нужен ради iOS: без него вместо первого кадра показывается
        // чёрный прямоугольник.
        return (
            <video
                className={className}
                src={`${media.source}#t=0.1`}
                preload="metadata"
                muted
                playsInline
                onLoadedMetadata={onReady}
                onError={onReady}
            />
        );
    }

    if (media.kind === MediaKind.gif && !playable) {
        return <GifFirstFrame src={media.source} className={className} onReady={onReady}/>;
    }

    return (
        <img
            className={className}
            src={resolveMediaUrl(media, type)}
            alt=""
            loading={lazy ? "lazy" : "eager"}
            onLoad={onReady}
            onError={onReady}
        />
    );
};

export default MediaFrame;
